<script>
/**
 * @component ValidationHistory
 * @description Historial de validaciones de prestaciones.
 * Doctor: ve solo las suyas con total valorizado del mes.
 * Admin/operador: ve todas, puede filtrar por prestador y período.
 */
import validationService from "@/services/validation";
import { mapGetters } from "vuex";
import * as XLSX from "xlsx";

const MONTHS = [
  "",
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const REJECTION_LABELS = {
  member_inactive: "Afiliado inactivo",
  member_no_plan: "Sin plan activo",
  invalid_token: "Token inválido",
  practice_not_covered: "Prestación no cubierta",
  provider_inactive: "Prestador inactivo",
  provider_no_contract: "Sin contrato vigente",
};

export default {
  name: "ValidationHistory",
  computed: {
    ...mapGetters(["isAdmin"]),
    /** @returns {string} Label del período seleccionado */
    periodLabel() {
      return `${MONTHS[this.filterMonth]} ${this.filterYear}`;
    },
    /** @returns {number} Total valorizado de las validaciones aprobadas visibles */
    totalApproved() {
      return this.validations
        .filter((v) => v.status === "approved")
        .reduce((sum, v) => sum + (v.valorization?.amount || 0), 0);
    },
  },
  data() {
    const now = new Date();
    return {
      validations: [],
      total: 0,
      page: 1,
      pageSize: 20,
      loading: false,
      exporting: false,
      filterStatus: "",
      filterYear: now.getFullYear(),
      filterMonth: now.getMonth() + 1,
      yearOptions: [now.getFullYear(), now.getFullYear() - 1],
      monthOptions: MONTHS.slice(1).map((name, i) => ({
        label: name,
        value: i + 1,
      })),
    };
  },
  created() {
    this.fetchValidations();
  },
  methods: {
    /**
     * @method fetchValidations
     * @desc Carga el historial con los filtros activos
     */
    async fetchValidations() {
      this.loading = true;
      try {
        const params = {
          offset: (this.page - 1) * this.pageSize,
          limit: this.pageSize,
          year: this.filterYear,
          month: this.filterMonth,
        };
        if (this.filterStatus) params.status = this.filterStatus;
        const res = await validationService.get(params);
        this.validations = res.docs || [];
        this.total = res.total || 0;
      } catch {
        this.$message.error("Error al cargar el historial");
      } finally {
        this.loading = false;
      }
    },

    /** @method onFilterChange */
    onFilterChange() {
      this.page = 1;
      this.fetchValidations();
    },

    /** @method onPageChange */
    onPageChange(page) {
      this.page = page;
      this.fetchValidations();
    },

    /** @method formatDate - Formato compacto AR: dd/mm/yy hh:mm */
    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },

    /** @method formatCurrency */
    formatCurrency(n) {
      return n != null ? `$${Number(n).toLocaleString("es-AR")}` : "-";
    },

    /**
     * @method getRejectionText
     * @param {string[]} reasons
     * @returns {string}
     */
    getRejectionText(reasons) {
      if (!reasons || !reasons.length) return "-";
      return reasons.map((r) => REJECTION_LABELS[r] || r).join(", ");
    },

    /**
     * @method liquidationLabel — UC-REG-178 Fase 1.
     * @param {string} status - pending | liquidated | invoiced | paid
     * @returns {string}
     */
    liquidationLabel(status) {
      return (
        {
          pending: "Pendiente",
          liquidated: "Liquidada",
          invoiced: "Facturada",
          sent_for_payment: "Enviada a pagar",
          paid: "Pagada",
        }[status] || "Pendiente"
      );
    },

    /**
     * @method downloadExcel — UC-REG-173. Descarga el historial del período
     *   filtrado en formato Excel con las columnas del importer del operador
     *   (Cuenta Médica → Nueva liquidación → Importar archivo). Sirve para
     *   que el prestador arme su lote de cobro sin tener que transcribir a
     *   mano — el operador después lo sube al importer y ya matchea.
     *
     *   Trae TODAS las filas del período (no paginado). Las rechazadas se
     *   incluyen con estado 'Rechazada' para trazabilidad; el operador puede
     *   filtrarlas en Excel antes de subirlo.
     */
    async downloadExcel() {
      this.exporting = true;
      try {
        const params = {
          offset: 0,
          limit: 9999,
          year: this.filterYear,
          month: this.filterMonth,
        };
        if (this.filterStatus) params.status = this.filterStatus;
        const res = await validationService.get(params);
        const rows = res.docs || [];
        if (!rows.length) {
          this.$message.warning(
            "No hay validaciones para exportar en el período"
          );
          return;
        }

        // Headers = ourFields del importer (mismos labels que en Cuenta Médica
        // → Submissions → Nueva → Importar archivo). Al mandar este Excel al
        // operador, el importer los detecta 1:1 sin ajuste manual.
        const headers = [
          "Fecha",
          "TRX",
          "DNI",
          "Nº afiliado",
          "Nombre paciente",
          "Plan",
          "Código nomenclador",
          "Código según prestador",
          "Denominación prestación",
          "Cantidad",
          "Monto declarado",
          "Estado",
          "Motivo rechazo",
          "Nº autorización",
        ];

        const dataAoA = rows.map((v) => {
          const m = v.memberSnapshot || {};
          const p = v.practiceSnapshot || {};
          const date = v.createdAt
            ? new Date(v.createdAt).toLocaleDateString("es-AR")
            : "";
          return [
            date,
            v.transactionCode || "",
            m.dni || "",
            m.memberNumber || "",
            [m.lastName, m.firstName].filter(Boolean).join(", "),
            m.planName || "",
            p.code || "",
            p.providerCode || "",
            p.providerName || p.name || "",
            1,
            v.status === "approved" ? Number(v.valorization?.amount) || 0 : 0,
            v.status === "approved" ? "Autorizada" : "Rechazada",
            v.status === "approved"
              ? ""
              : this.getRejectionText(v.rejectionReasons),
            v.authorizationCode || "",
          ];
        });

        const aoa = [headers, ...dataAoA];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        // Ancho de columnas para que sea legible al abrir
        ws["!cols"] = [
          { wch: 11 }, // Fecha
          { wch: 12 }, // TRX
          { wch: 12 }, // DNI
          { wch: 14 }, // Nº afiliado
          { wch: 26 }, // Nombre
          { wch: 14 }, // Plan
          { wch: 18 }, // Cód nomenclador
          { wch: 18 }, // Cód prestador
          { wch: 40 }, // Denominación
          { wch: 8 }, // Cant
          { wch: 14 }, // Monto
          { wch: 12 }, // Estado
          { wch: 24 }, // Motivo
          { wch: 16 }, // Nº autorización
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Prestaciones");

        const monthName = MONTHS[this.filterMonth];
        const fileName = `prestaciones_${monthName}_${this.filterYear}.xlsx`;
        XLSX.writeFile(wb, fileName);

        this.$message.success(`Excel descargado (${rows.length} filas)`);
      } catch (err) {
        this.$message.error(
          err.response?.data?.error || "Error al generar el Excel"
        );
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<template lang="pug">
  div
    //- Filtros
    .history-toolbar
      .history-toolbar__filters
        el-select(
          v-model="filterYear"
          size="small"
          style="width:100px"
          @change="onFilterChange"
        )
          el-option(v-for="y in yearOptions" :key="y" :label="y" :value="y")
        el-select(
          v-model="filterMonth"
          size="small"
          style="width:130px"
          @change="onFilterChange"
        )
          el-option(
            v-for="m in monthOptions"
            :key="m.value"
            :label="m.label"
            :value="m.value"
          )
        el-select(
          v-model="filterStatus"
          placeholder="Todos los estados"
          clearable size="small"
          style="width:180px"
          @change="onFilterChange"
        )
          el-option(label="Autorizadas" value="approved")
          el-option(label="Rechazadas" value="rejected")

      //- Resumen del período + export
      .history-toolbar__summary(v-if="validations.length")
        span.summary-label {{ periodLabel }}
        span.summary-divider ·
        span.summary-count {{ total }} validaciones
        span.summary-divider ·
        span.summary-amount {{ formatCurrency(totalApproved) }} autorizado
        el-button.excel-btn(
          size="small"
          type="primary"
          plain
          icon="el-icon-download"
          :loading="exporting"
          @click="downloadExcel"
        ) Descargar Excel

    //- Tabla
    el-table(
      :data="validations"
      v-loading="loading"
      border
      size="small"
      style="margin-top:16px"
    )
      el-table-column(label="Fecha" width="130")
        template(slot-scope="{ row }") {{ formatDate(row.createdAt) }}

      el-table-column(label="TRX" width="110")
        template(slot-scope="{ row }")
          span.cell-trx(v-if="row.transactionCode") {{ row.transactionCode }}
          span(v-else style="color:#c0c4cc") —

      el-table-column(label="Origen" width="140")
        template(slot-scope="{ row }")
          span.cell-origin(v-if="row._sourceType === 'authorization'") Autorización
          span.cell-origin.cell-origin--direct(v-else) Validación directa

      el-table-column(label="Afiliado" min-width="210")
        template(slot-scope="{ row }")
          //- 1 sola línea: "Apellido, Nombre · Plan"
          span.cell-affil(v-if="row.memberSnapshot")
            span.cell-affil__name {{ row.memberSnapshot.lastName }}, {{ row.memberSnapshot.firstName }}
            span.cell-affil__plan(v-if="row.memberSnapshot.planName")  · {{ row.memberSnapshot.planName }}
          span(v-else) -

      el-table-column(label="Prestación" min-width="360")
        template(slot-scope="{ row }")
          //- UC-REG-165 — Layout 2 líneas: códigos arriba (prestador navy +
          //- chip teal financiador), denominación abajo. Fonts chicos para
          //- mantener la fila compacta.
          div.p-cell(v-if="row.practiceSnapshot")
            .p-cell__codes
              //- Fallback silencioso: si el ítem del contrato no tiene
              //- providerCode cargado, se muestra el del financiador
              //- también en la casilla navy (queda repetido, es esperado).
              span.p-code-prov {{ row.practiceSnapshot.providerCode || row.practiceSnapshot.code }}
              span.p-code-fin {{ row.practiceSnapshot.code }}
            //- Nombre colapsa con ellipsis; hover muestra completo (title nativo)
            .p-cell__name(:title="row.practiceSnapshot.providerName || row.practiceSnapshot.name") {{ row.practiceSnapshot.providerName || row.practiceSnapshot.name }}
          span(v-else) -

      el-table-column(label="Prestador" v-if="isAdmin" min-width="160")
        template(slot-scope="{ row }")
          span(v-if="row.providerSnapshot")
            | {{ row.providerSnapshot.lastName }}, {{ row.providerSnapshot.firstName }}
          span(v-else) -

      el-table-column(label="Estado" width="110")
        template(slot-scope="{ row }")
          el-tag(
            :type="row.status === 'approved' ? 'success' : 'danger'"
            size="small"
          )
            | {{ row.status === 'approved' ? 'Autorizada' : 'Rechazada' }}

      el-table-column(label="Valor / Motivo" width="140")
        template(slot-scope="{ row }")
          div(v-if="row.status === 'approved'")
            span.cell-amount {{ formatCurrency(row.valorization && row.valorization.amount) }}
            span.cell-copago(v-if="row.valorization && row.valorization.hasCopago")
              |  · Copago {{ formatCurrency(row.valorization.copagoAmount) }}
          div.cell-rejection(v-else) {{ getRejectionText(row.rejectionReasons) }}

      //- UC-REG-178 Fase 1 — chip Liquidación (solo TRXs aprobadas).
      //-   Deja saber al prestador si su prestación ya se liquidó, facturó o pagó.
      el-table-column(label="Liquidación" width="120")
        template(slot-scope="{ row }")
          span.liq-chip(
            v-if="row.status === 'approved'"
            :class="'liq-chip--' + (row.liquidationStatus || 'pending')"
          ) {{ liquidationLabel(row.liquidationStatus) }}
          span.muted(v-else) —

    //- Paginación
    .pagination-wrapper
      el-pagination(
        v-if="total > pageSize"
        layout="prev, pager, next, total"
        :total="total"
        :page-size="pageSize"
        :current-page="page"
        @current-change="onPageChange"
      )

    //- Vacío
    .empty-state(v-if="!loading && validations.length === 0")
      i.el-icon-document
      p No hay validaciones para el período seleccionado
</template>

<style lang="scss" scoped>
.history-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;

  &__filters {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__summary {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
  }
}

.summary-label {
  font-weight: 700;
  color: #002d5e;
}
.summary-divider {
  color: #cbd5e1;
}
.summary-count {
  color: #6b7294;
}
.summary-amount {
  font-weight: 700;
  color: #059669;
}
.excel-btn {
  margin-left: 12px;
}

/* UC-REG-178 Fase 1 — chip Liquidación */
.liq-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid transparent;
}
.liq-chip--pending {
  background: #f1f5f9;
  color: #475569;
  border-color: #cbd5e1;
}
.liq-chip--liquidated {
  background: #ecfdf5;
  color: #065f46;
  border-color: #a7f3d0;
}
.liq-chip--invoiced {
  background: #dbeafe;
  color: #1e40af;
  border-color: #93c5fd;
}
.liq-chip--sent_for_payment {
  background: #ede9fe;
  color: #5b21b6;
  border-color: #c4b5fd;
}
.liq-chip--paid {
  background: #d1fae5;
  color: #065f46;
  border-color: #6ee7b7;
}
.muted {
  color: #94a3b8;
}

.cell-primary {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #002d5e;
}
.cell-secondary {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  color: #6b7294;
}
.cell-code {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #002d5e;
  margin-right: 4px;
}

// UC-REG-165 — Celda "Afiliado" en 1 sola línea: "Apellido, Nombre · Plan".
.cell-affil {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  &__name {
    font-size: 13px;
    font-weight: 500;
    color: #1a1f36;
  }
  &__plan {
    font-size: 12px;
    color: #6b7294;
  }
}

// UC-REG-165 — Celda "Prestación" en 2 filas: códigos arriba (compactos),
// denominación abajo. Fonts chicos para no ensanchar la columna.
.p-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
  &__codes {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }
  &__name {
    font-size: 12px;
    color: #1a1f36;
    line-height: 1.35;
    // Colapsar a 1 línea con ellipsis. El title="" nativo muestra el
    // texto completo al hover — sin dependencia de el-tooltip.
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
}
.p-code-prov {
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  font-weight: 600;
  color: #002d5e;
}
.p-code-fin {
  font-variant-numeric: tabular-nums;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 3px;
  font-weight: 500;
  white-space: nowrap;
  background: #ccfbf1;
  color: #115e59;
  line-height: 1.4;
}
.cell-amount {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #059669;
}
.cell-copago {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 11px;
  color: #6b7294;
}
.cell-origin {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: #eeedfe;
  color: #26215c;
}
.cell-origin--direct {
  background: #e1f5ee;
  color: #085041;
}
.cell-trx {
  font-family: monospace;
  font-size: 12px;
  font-weight: 700;
  color: #002d5e;
}
.cell-rejection {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  color: #dc2626;
}

.pagination-wrapper {
  margin-top: 16px;
  text-align: center;
}

.empty-state {
  text-align: center;
  padding: 48px 0;
  color: #94a3b8;
  i {
    font-size: 40px;
    display: block;
    margin-bottom: 12px;
  }
  p {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    margin: 0;
  }
}
</style>
